import { isNativeApp, nativeResumeEvent } from "./lib/native-app";
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { AuthCallback } from "./components/AuthCallback";
import { LoginScreen } from "./components/LoginScreen";
import { SetPassword } from "./components/SetPassword";
import { PublicMonthlyFoodForm } from "./components/PublicMonthlyFoodForm";
import { apiFetch, enableDirectApiSession, type ApiSessionInfo, type ApiUser } from "./lib/api";
import { checkSession, initialSessionCheckTimeoutMs, isExpiredSession } from "./lib/session-check";
import { PlatformToast, usePlatformToast } from "./components/PlatformToast";
import { currentDeploymentEntryAsset, latestDeploymentEntryAsset } from "./lib/deployment-update";
import { loadDashboard, preloadDashboard } from "./lib/dashboard-loader";
import {
  clearToken,
  consumeSessionExpiredNotice,
  hasRecentSession,
  markSessionExpired,
  readSessionExpiresAt,
  readSessionIdleTimeout,
  readSessionLastActivity,
  recordSessionActivity,
  saveSessionInfo,
  sessionExpiredEvent,
  sessionWarningBeforeMs,
} from "./lib/auth";
import { saveLocale, translateUi, translations, type Locale } from "./lib/i18n";
import { usePlatformLocale } from "./lib/platform-language";
import { useRoute } from "./lib/router";
import { applyThemeMode, readThemeMode, saveThemeMode, type ThemeMode } from "./lib/theme";

const Dashboard = lazy(() => loadDashboard().then((module) => ({ default: module.Dashboard })));

type AuthState =
  | { status: "loading"; user: null; token: string | null }
  | { status: "unavailable"; user: null; token: null }
  | { status: "guest"; user: null; token: null }
  | { status: "authenticated"; user: ApiUser; token: string };

export function App() {
  const locale = usePlatformLocale();
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => readThemeMode());
  const [auth, setAuth] = useState<AuthState>({ status: "loading", user: null, token: null });
  const [sessionCheckAttempt, setSessionCheckAttempt] = useState(0);
  const { toast, showError, dismiss } = usePlatformToast();
  const [sessionExpiredNotice, setSessionExpiredNotice] = useState(() => consumeSessionExpiredNotice());
  const [sessionWarning, setSessionWarning] = useState(false);
  const [deploymentUpdateAvailable, setDeploymentUpdateAvailable] = useState<string | null>(null);
  const dismissedDeploymentEntry = useRef<string | null>(null);
  const heartbeatInFlight = useRef(false);
  const lastHeartbeatAt = useRef(0);
  const route = useRoute();

  const clearCurrentSession = useCallback(() => {
    clearToken();
    setSessionWarning(false);
    setAuth({ status: "guest", user: null, token: null });
    if (window.location.pathname !== "/") window.history.replaceState({}, "", "/");
  }, []);

  const expireCurrentSession = useCallback(() => {
    markSessionExpired();
    setSessionExpiredNotice(true);
    clearCurrentSession();
  }, [clearCurrentSession]);

  const logoutCurrentSession = useCallback(() => {
    setSessionExpiredNotice(false);
    void apiFetch<{ ok: true }>("/api/auth/logout", { method: "POST" })
      .catch(() => undefined)
      .finally(() => {
        clearCurrentSession();
        window.location.assign("/");
      });
  }, [clearCurrentSession]);

  function changeLocale(nextLocale: Locale) {
    saveLocale(nextLocale);
  }

  useEffect(() => {
    document.documentElement.lang = locale;
    document.title = translations[locale].productName;
  }, [locale]);

  function changeThemeMode(nextMode: ThemeMode) {
    saveThemeMode(nextMode);
    setThemeMode(nextMode);
  }

  useEffect(() => {
    applyThemeMode(themeMode);
    const interval = window.setInterval(() => applyThemeMode(themeMode), 60_000);
    return () => window.clearInterval(interval);
  }, [themeMode]);

  useEffect(() => {
    if (!import.meta.env.PROD || isNativeApp()) return;
    const currentEntry = currentDeploymentEntryAsset();
    if (!currentEntry) return;
    const controller = new AbortController();
    let checking = false;

    const check = async () => {
      if (checking || controller.signal.aborted) return;
      checking = true;
      try {
        const latestEntry = await latestDeploymentEntryAsset(controller.signal);
        if (latestEntry && !controller.signal.aborted) {
          setDeploymentUpdateAvailable(latestEntry !== currentEntry && latestEntry !== dismissedDeploymentEntry.current ? latestEntry : null);
        }
      } catch {
        // A failed update check must not interrupt normal platform use.
      } finally {
        checking = false;
      }
    };
    const checkWhenVisible = () => {
      if (document.visibilityState === "visible") void check();
    };
    const initialCheck = window.setTimeout(() => void check(), 10_000);
    const interval = window.setInterval(() => void check(), 2 * 60_000);
    window.addEventListener("focus", checkWhenVisible);
    document.addEventListener("visibilitychange", checkWhenVisible);
    return () => {
      controller.abort();
      window.clearTimeout(initialCheck);
      window.clearInterval(interval);
      window.removeEventListener("focus", checkWhenVisible);
      document.removeEventListener("visibilitychange", checkWhenVisible);
    };
  }, []);

  function dismissDeploymentUpdate() {
    dismissedDeploymentEntry.current = deploymentUpdateAvailable;
    setDeploymentUpdateAvailable(null);
  }

  useEffect(() => {
    if (route.name !== "app") return;
    let cancelled = false;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), initialSessionCheckTimeoutMs);
    setAuth({ status: "loading", user: null, token: null });
    if (hasRecentSession()) preloadDashboard();
    void checkSession(() => apiFetch<{ user: ApiUser; session: ApiSessionInfo }>("/api/auth/me", { suppressUnauthorizedSessionEvent: true, signal: controller.signal }))
      .then((result) => {
        window.clearTimeout(timeout);
        if (cancelled) return;
        if (result.status === "authenticated") {
          const { user, session } = result.value;
          saveSessionInfo(session);
          setAuth({ status: "authenticated", user, token: "" });
          void enableDirectApiSession(user.id);
        } else if (result.status === "guest") {
          const expired = consumeSessionExpiredNotice();
          clearToken();
          setSessionExpiredNotice(expired);
          setAuth({ status: "guest", user: null, token: null });
        } else {
          setAuth({ status: "unavailable", user: null, token: null });
          if (sessionCheckAttempt > 0) showError(translateUi("Hyrja nuk u kontrollua. Prit pak dhe provo përsëri.", "Sign-in could not be checked. Wait a moment and try again."));
        }
      });
    return () => { cancelled = true; window.clearTimeout(timeout); controller.abort(); };
  }, [route.name, sessionCheckAttempt, showError]);

  useEffect(() => {
    const handleExpired = () => expireCurrentSession();
    window.addEventListener(sessionExpiredEvent, handleExpired);
    return () => window.removeEventListener(sessionExpiredEvent, handleExpired);
  }, [expireCurrentSession]);

  useEffect(() => {
    if (auth.status !== "authenticated") return;
    let lastRecordedInput = 0;

    const heartbeat = async (force = false) => {
      const now = Date.now();
      if (heartbeatInFlight.current || (!force && now - lastHeartbeatAt.current < 5 * 60 * 1000)) return;
      heartbeatInFlight.current = true;
      lastHeartbeatAt.current = now;
      try {
        const result = await apiFetch<{ ok: true; session: ApiSessionInfo }>("/api/auth/session/heartbeat", {
          method: "POST",
        });
        saveSessionInfo(result.session);
      } catch {
        // A 401 is handled globally; transient network errors must not end a valid session.
      } finally {
        heartbeatInFlight.current = false;
      }
    };

    const recordActivity = () => {
      const now = Date.now();
      if (now - lastRecordedInput < 1_000) return;
      lastRecordedInput = now;
      recordSessionActivity(now);
      setSessionWarning(false);
    };

    const checkSession = () => {
      const now = Date.now();
      const expiresAt = readSessionExpiresAt();
      const idleTimeout = readSessionIdleTimeout();
      const lastActivity = readSessionLastActivity() || now;
      const idleFor = now - lastActivity;
      if ((expiresAt !== null && now >= expiresAt) || idleFor >= idleTimeout) {
        expireCurrentSession();
        return;
      }
      setSessionWarning(idleFor >= idleTimeout - sessionWarningBeforeMs);
      if (idleFor < 2 * 60 * 1000) void heartbeat();
    };

    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return;
      checkSession();
      recordActivity();
    };

    const activityEvents: Array<keyof WindowEventMap> = ["pointerdown", "keydown", "touchstart", "scroll"];
    activityEvents.forEach((eventName) => window.addEventListener(eventName, recordActivity, { passive: true }));
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener(nativeResumeEvent, handleVisibility);
    checkSession();
    const interval = window.setInterval(checkSession, 15_000);

    return () => {
      window.clearInterval(interval);
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, recordActivity));
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener(nativeResumeEvent, handleVisibility);
    };
  }, [auth, expireCurrentSession]);

  async function continueSession() {
    if (auth.status !== "authenticated") return;
    recordSessionActivity();
    setSessionWarning(false);
    try {
      const result = await apiFetch<{ ok: true; session: ApiSessionInfo }>("/api/auth/session/heartbeat", {
        method: "POST",
      });
      saveSessionInfo(result.session);
      lastHeartbeatAt.current = Date.now();
    } catch (error) {
      if (isExpiredSession(error)) expireCurrentSession();
      else showError(translateUi("Sesioni nuk u konfirmua. Prit pak dhe provo përsëri.", "The session could not be confirmed. Wait a moment and try again."));
    }
  }

  if (route.name === "callback") return <AuthCallback locale={locale} />;
  if (route.name === "set-password") return <SetPassword locale={locale} themeMode={themeMode} onThemeChange={changeThemeMode} />;
  if (route.name === "monthly-food") return <PublicMonthlyFoodForm />;

  const bootScreen = <main className="boot-screen"><img src="/assets/bgt-logo.png" alt="BGT" /><span className="loader" aria-label={translations[locale].loading} /></main>;
  if (auth.status === "loading") return bootScreen;

  if (auth.status === "unavailable") {
    return <main className="callback-screen">
      <section className="callback-state" role="alert">
        <h1>{translateUi("Hyrja nuk u kontrollua", "Sign-in could not be checked", locale)}</h1>
        <p>{translateUi("Shërbimi përkohësisht nuk përgjigjet. Prit pak dhe provo përsëri.", "The service is temporarily unavailable. Wait a moment and try again.", locale)}</p>
        <button type="button" className="primary-action compact" onClick={() => setSessionCheckAttempt((attempt) => attempt + 1)}>{translateUi("Provo përsëri", "Try again", locale)}</button>
      </section>
      <PlatformToast toast={toast} onDismiss={dismiss} />
    </main>;
  }

  if (auth.status === "authenticated") {
    return (
      <>
        <Suspense fallback={bootScreen}>
          <Dashboard
            locale={locale}
            onLocaleChange={changeLocale}
            themeMode={themeMode}
            onThemeChange={changeThemeMode}
            user={auth.user}
            token={auth.token}
            onLogout={logoutCurrentSession}
          />
        </Suspense>
        <PlatformToast toast={toast} onDismiss={dismiss} />
        {sessionWarning ? (
          <div className="session-warning-backdrop" role="presentation">
            <section className="session-warning-dialog" role="alertdialog" aria-modal="true" aria-labelledby="session-warning-title">
              <span className="form-eyebrow">{translations[locale].sessionSecurity}</span>
              <h2 id="session-warning-title">{translations[locale].sessionExpiresSoon}</h2>
              <p>{translations[locale].sessionExpiresSoonText}</p>
              <div className="session-warning-actions">
                <button type="button" className="secondary-action" onClick={logoutCurrentSession}>{translations[locale].logout}</button>
                <button type="button" className="primary-action" onClick={() => void continueSession()}>{translations[locale].continueSession}</button>
              </div>
            </section>
          </div>
        ) : null}
        {deploymentUpdateAvailable ? (
          <section className="platform-update-notice" role="status" aria-live="polite">
            <span><strong>{locale === "sq" ? "Version i ri është gati" : "A new version is ready"}</strong><small>{locale === "sq" ? "Ruaj punën para përditësimit. Mund ta mbyllësh njoftimin dhe të vazhdosh." : "Save your work before updating. You can close this notice and continue."}</small></span>
            <div className="platform-update-notice-actions">
              <button type="button" className="secondary-action compact" onClick={dismissDeploymentUpdate} aria-label={locale === "sq" ? "Mbyll njoftimin pa përditësuar faqen" : "Close this notice without updating the page"}>{locale === "sq" ? "Mbyll" : "Close"}</button>
              <button type="button" className="primary-action compact" onClick={() => window.location.reload()}>{locale === "sq" ? "Përditëso tani" : "Update now"}</button>
            </div>
          </section>
        ) : null}
      </>
    );
  }

  return (
    <LoginScreen
      locale={locale}
      onLocaleChange={changeLocale}
      themeMode={themeMode}
      onThemeChange={changeThemeMode}
      sessionExpiredNotice={sessionExpiredNotice}
    />
  );
}
